import { useQuery } from "@apollo/client/react";
import { UsageTrackerEvent, usageTracker } from "@volumental/analytic";
import introBackground from "@volumental/assets/images/scanner/IntroPage.png";
import {
  Button,
  Container,
  Content,
  ErrorText,
  Headline,
  Loader,
  ScanList,
  Text,
  TextButton,
} from "@volumental/common";
import { useCallback, useEffect, useState } from "react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { FitSessionType } from "../../api/gql/graphql";
import { GetScansQuery } from "../../api/queries/scan-query";
import { tokenStore } from "../../auth/token-store";
import { useDeviceAuth } from "../../auth/use-device-auth";
import { Header } from "../../components/accessible-components";
import { useHistoryStore, useInputStore, useScannerStore, useSessionStore, useViewStore } from "../../store";
import { CurrentView, UserFlowType } from "../../types/types";
import type { ScanListItem as Scan } from "../../types/types";

interface SelectScanProps {
  onClose: () => void;
}

export function SelectScan({ onClose }: SelectScanProps): JSX.Element {
  const { t } = useTranslation();
  const { email, scanID, setEmail, setScanID, clear, setPreviousPurchaseID } = useInputStore((store) => store);
  const { setFlowType } = useScannerStore((store) => store);
  const { storeFinderUrl } = useSessionStore((store) => store);
  const { clearScans, setScans, scans } = useHistoryStore((store) => store);
  const { setCurrentView } = useViewStore((store) => store);
  const [selectedItem, setSelectedItem] = useState<Scan | undefined>();
  const { data, error, loading, refetch } = useQuery(GetScansQuery, {
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  useEffect(() => {
    if (!tokenStore.getToken()) {
      setCurrentView(CurrentView.Intro);
    }
  }, [setCurrentView]);

  const handleAuthSuccess = useCallback(() => {
    usageTracker.track(UsageTrackerEvent.VerifiedEmail);
    void refetch();
  }, [refetch]);

  const {
    startAuth,
    cancel,
    isPending,
    error: authError,
  } = useDeviceAuth({
    clientId: "visa-web-side-bar",
    onSuccess: handleAuthSuccess,
  });

  const saveAndShowResult = (scan: Scan): void => {
    usageTracker.track(UsageTrackerEvent.SelectScan, {
      scan_id: scan.id,
      scan_type: scan.type,
    });
    setPreviousPurchaseID(scan.previousPurchaseID);
    setScanID(scan.id);
    setFlowType(scan.type === "RetailScan" ? UserFlowType.HistoryRScan : UserFlowType.HistoryMScan);
    setCurrentView(CurrentView.Result);
  };

  const onChangeEmail = (): void => {
    clear();
    if (isPending) {
      void startAuth();
      return;
    }
    cancel();
    tokenStore.clearToken();
    void startAuth();
  };

  const changeEmailLabel = isPending ? t("completeSignInInPopup") : t("button.useADifferentEmail");

  useEffect(() => {
    const me = data?.me;
    if (me) {
      clearScans();
      const s: Scan[] = [];
      for (const fitSession of me.fitSessions) {
        if (fitSession.scanID) {
          s.push({
            type: fitSession.fitSessionType === FitSessionType.RetailScan ? "RetailScan" : "WebScan",
            id: fitSession.scanID,
            createdAt: fitSession.createdAt as string,
            previousPurchaseID: fitSession.previousPurchaseID ?? undefined,
          });
        }
      }
      if (me.email) {
        setEmail(me.email);
      }
      setSelectedItem(s.find((c) => c.id === scanID) ?? s[0]);
      setScans(s);
      usageTracker.track(UsageTrackerEvent.ShowScanList, {
        scan_count: s.length,
      });
    }
  }, [clearScans, data, scanID, setEmail, setScans]);

  const footerContent = (
    <div className="relative flex flex-auto flex-col grow justify-end px-6 my-2.5 before:absolute before:content-[''] before:z-1 before:-top-8 before:left-0 before:w-full before:h-8 before:bg-linear-to-t before:from-white/50 before:to-transparent">
      {scans.length > 0 ? (
        <Button
          type="button"
          onClick={() => {
            if (selectedItem?.id) {
              saveAndShowResult(selectedItem);
            }
          }}
          loading={loading}
        >
          {t("button.useThisScan")}
        </Button>
      ) : (
        storeFinderUrl && (
          <Button
            type="button"
            onClick={() => {
              if (storeFinderUrl && window.top) {
                usageTracker.track(UsageTrackerEvent.OpenStoreFinder);
                window.top.location.href = storeFinderUrl;
              }
            }}
          >
            {t("findStore")}
          </Button>
        )
      )}
      <TextButton onClick={onChangeEmail}>{changeEmailLabel}</TextButton>
      {authError && (
        <ErrorText>{t(authError === "popup-blocked" ? "signInPopupBlocked" : "SomethingWentWrong")}</ErrorText>
      )}
    </div>
  );

  if (loading) {
    return (
      <Container>
        <Header close={onClose} />
        <Loader />
      </Container>
    );
  }

  return (
    <Container>
      <Header close={onClose} />
      {scans.length === 0 ? (
        <Content>
          <Headline>{t("noScanResults")}</Headline>
          <Text>{t("noScanResultsFound")}</Text>
          <img src={introBackground} alt={t("imageAlt.selectScanIntro")} className="rounded-3xl mt-7.5 mb-15" />
          <Button
            loading={false}
            type="submit"
            onClick={() => {
              setCurrentView(CurrentView.Intro);
            }}
          >
            {t("button.getStarted")}
          </Button>
          <TextButton onClick={onChangeEmail}>{changeEmailLabel}</TextButton>
          {authError && (
            <ErrorText>{t(authError === "popup-blocked" ? "signInPopupBlocked" : "SomethingWentWrong")}</ErrorText>
          )}
        </Content>
      ) : (
        <>
          <Content>
            <div className="after:z-1 after:content-[''] after:absolute after:left-0 after:top-14 after:h-4.5 after:w-full after:bg-linear-to-b after:from-white after:to-transparent">
              <Headline>{t("yourScanHistory")}</Headline>
              {Boolean(error) && <ErrorText>{t("somethingWentWrongFinding")}</ErrorText>}
              <Text>
                {`${t("hereAreAllScans")} `}
                <strong>{email}</strong>
                {`. ${t("pleaseSelectAFootScan")}`}
              </Text>
              <ScanList
                items={scans}
                selectedId={selectedItem?.id}
                onSelect={(id: string) => {
                  setSelectedItem(scans.find((scan) => scan.id === id));
                }}
                titles={{
                  instore: t("instoreFootScan"),
                  webscanner: t("onlineFootScan"),
                }}
                selectIconAlt={t("imageAlt.selectScan")}
              />
            </div>
          </Content>
          {footerContent}
        </>
      )}
    </Container>
  );
}
